import { Link, useParams } from "react-router-dom";
import { useEffect } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, ClipImage } from "../components/Reveal";
import { CTABlock } from "../components/CTABlock";
import { ARTICLES, fmtDate } from "../data/insights";
import { SERVICES } from "../data/services";
import { INDUSTRY_NAME_PAGE } from "../data/industries";
import { SITE } from "../data/site";
import { track } from "../lib/analytics";
import NotFound from "./NotFound";

function Block({ b }) {
  if (b.t === "h2") {
    return <h2 className="mt-14 font-display text-2xl font-semibold uppercase tracking-tight text-charcoal sm:text-3xl">{b.text}</h2>;
  }
  if (b.t === "h3") {
    return <h3 className="mt-10 font-display text-xl font-semibold uppercase tracking-tight text-charcoal">{b.text}</h3>;
  }
  if (b.t === "ul") {
    return (
      <ul className="my-7 space-y-3">
        {b.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-zinc-700">
            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 bg-dcc" aria-hidden="true" />{it}
          </li>
        ))}
      </ul>
    );
  }
  return <p className="my-7 text-base leading-[1.85] text-zinc-700">{b.text}</p>;
}

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 25 });

  // article view + scroll-depth (25/50/75/100) analytics
  useEffect(() => {
    if (!article) return undefined;
    track("insight_article_view", { slug: article.slug, category: article.category, path: window.location.pathname });
    const marks = [25, 50, 75, 100];
    const fired = new Set();
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = Math.round((window.scrollY / max) * 100);
      marks.forEach((m) => {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          track("insight_scroll_depth", { slug: article.slug, depth: m });
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [article]);

  if (!article) return <NotFound />;

  const service = SERVICES.find((s) => s.id === article.serviceId);
  const industryName = service ? service.industries[0] : null;
  const industrySlug = industryName ? INDUSTRY_NAME_PAGE[industryName] : null;
  const related = [
    ...ARTICLES.filter((a) => a.slug !== slug && a.category === article.category),
    ...ARTICLES.filter((a) => a.slug !== slug && a.category !== article.category),
  ].slice(0, 3);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.meta,
      image: `${SITE.url}${article.image}`,
      author: { "@type": "Organization", name: article.author, url: SITE.url },
      publisher: { "@type": "Organization", name: SITE.name, logo: { "@type": "ImageObject", url: `${SITE.url}/assets/dcc-logo.webp` } },
      articleSection: article.category,
      ...(article.datePublished ? { datePublished: article.datePublished } : {}),
      ...(article.dateModified ? { dateModified: article.dateModified } : {}),
      mainEntityOfPage: `${SITE.url}/insights/${article.slug}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
        { "@type": "ListItem", position: 2, name: "Insights", item: `${SITE.url}/insights` },
        { "@type": "ListItem", position: 3, name: article.title, item: `${SITE.url}/insights/${article.slug}` },
      ],
    },
  ];

  return (
    <>
      <SEO title={article.seoTitle || `${article.title} | DCC Insights`} description={article.meta} path={`/insights/${article.slug}`} type="article" jsonLd={jsonLd} image={`${SITE.url}${article.image}`} />

      {/* reading progress */}
      <motion.div aria-hidden="true" className="fixed inset-x-0 top-0 z-[70] h-[3px] origin-left bg-dcc" style={{ scaleX: progress }} />

      <article className="bg-paper pt-32 md:pt-44">
        <div className="container-x">
          <Reveal>
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
              <Link to="/insights" className="transition-colors hover:text-dcc-strong">Insights</Link>
              <span aria-hidden="true">/</span>
              <span className="text-dcc-strong">{article.category}</span>
            </nav>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="h-display mt-8 max-w-4xl text-4xl normal-case leading-[1.02] text-charcoal sm:text-5xl">{article.title}</h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
              <span className="text-charcoal">{article.author}</span>
              <span aria-hidden="true">·</span>
              <span>{article.authorRole}</span>
              <span aria-hidden="true">·</span>
              <span>{article.readMinutes} min read</span>
              {article.datePublished && (<><span aria-hidden="true">·</span><span>Published <time dateTime={article.datePublished}>{fmtDate(article.datePublished)}</time></span></>)}
              {article.dateModified && (<><span aria-hidden="true">·</span><span>Updated <time dateTime={article.dateModified}>{fmtDate(article.dateModified)}</time></span></>)}
            </p>
            <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Technical review: {article.reviewer}</p>
          </Reveal>
          <Reveal delay={0.22}>
            <figure className="mt-12">
              <ClipImage src={article.image} alt={article.imageAlt} ratio="aspect-[21/9]" width={article.imageW} height={article.imageH} className="border border-zinc-200" />
              <figcaption className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Editorial illustration — not project evidence</figcaption>
            </figure>
          </Reveal>

          <div className="grid gap-14 py-16 md:py-20 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <Reveal>
                <p className="text-lg leading-relaxed text-charcoal md:text-xl">{article.standfirst}</p>
              </Reveal>
              <div className="mt-4">
                {article.blocks.map((b, i) => <Block key={i} b={b} />)}
              </div>

              {/* related service */}
              <Reveal>
                <div className="mt-14 border border-zinc-200 bg-offwhite p-8">
                  <p className="eyebrow">Related service</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {service ? (
                      <Link to={`/services/${service.slug}`} data-testid="article-service-link"
                        className="border border-zinc-300 bg-white px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-zinc-700 transition-colors hover:border-dcc hover:text-dcc-strong">
                        {service.name}
                      </Link>
                    ) : (
                      <Link to="/services" data-testid="article-service-link"
                        className="border border-zinc-300 bg-white px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-zinc-700 transition-colors hover:border-dcc hover:text-dcc-strong">
                        All DCC Services
                      </Link>
                    )}
                    <Link to={industrySlug ? `/industries/${industrySlug}` : "/industries"} data-testid="article-industry-link"
                      className="border border-zinc-300 bg-white px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-zinc-700 transition-colors hover:border-dcc hover:text-dcc-strong">
                      {industryName || "All industries"}
                    </Link>
                    <Link to="/request-survey" data-testid="article-survey-cta"
                      className="border border-dcc bg-dcc px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-dcc-hover">
                      Request a Free Site Survey
                    </Link>
                  </div>
                </div>
              </Reveal>
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-32">
                <p className="eyebrow">Related reading</p>
                <div className="mt-6 space-y-0">
                  {related.map((r) => (
                    <Link key={r.slug} to={`/insights/${r.slug}`} data-testid={`article-related-${r.slug}`}
                      className="group block border-b border-zinc-200 py-5">
                      <p className="font-mono text-xs uppercase tracking-[0.2em] text-dcc-strong">{r.category}</p>
                      <p className="mt-2 font-display text-lg font-medium uppercase leading-snug tracking-tight text-charcoal transition-colors group-hover:text-dcc-strong">{r.title}</p>
                      <p className="mt-1 font-mono text-xs text-zinc-500">{r.readMinutes} min read</p>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>
      <CTABlock dark />
    </>
  );
}
